import intl from "react-intl-universal";

const TONE_STYLES = {
  pending: {
    card: "border-amber-200 bg-amber-50/80 dark:border-amber-900/50 dark:bg-amber-950/30",
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
    label: "text-amber-700 dark:text-amber-400",
  },
  approved: {
    card: "border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/50 dark:bg-emerald-950/30",
    badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
    label: "text-emerald-700 dark:text-emerald-400",
  },
  rejected: {
    card: "border-red-200 bg-red-50/80 dark:border-red-900/50 dark:bg-red-950/30",
    badge: "bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300",
    label: "text-red-700 dark:text-red-400",
  },
};

const CHIP_STYLES = {
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  approved: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  rejected: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
};

const resolveSituationLabel = (situation) => {
  if (!situation) return null;
  const statusId = situation.statusId;
  const localeKey =
    statusId != null ? `baas.proposalSituation.status.${statusId}` : "";
  const localized =
    localeKey && intl.get(localeKey) && intl.get(localeKey) !== localeKey
      ? intl.get(localeKey)
      : null;
  return (
    localized
    || situation.description
    || (statusId != null ? String(statusId) : null)
  );
};

/**
 * Compact chip for grids — same labels/colors as the detail card.
 */
export const BaasProposalSituationBadge = ({
  situation = null,
  className = "",
  showLabel = false,
}) => {
  if (!situation) return null;

  const tone = situation.tone || "pending";
  const label = resolveSituationLabel(situation);
  if (!label) return null;

  return (
    <span className={`inline-flex flex-nowrap items-center gap-1.5 ${className}`}>
      {showLabel ? (
        <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {intl.get("baas.proposalSituation.label")}
        </span>
      ) : null}
      <span
        className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${
          CHIP_STYLES[tone] ?? CHIP_STYLES.pending
        }`}
        title={intl.get("baas.proposalSituation.label")}
      >
        {label}
      </span>
    </span>
  );
};

/**
 * Detail status card — separates entity KYC vs Conta operacional when relevant.
 * When PaySmart proposal exists but Valut has no local Account, clarify that Conta
 * chip is Sem conta (proposal alone must not drive Conta).
 */
export const BaasProposalSituationCard = ({
  situation = null,
  className = "",
  compact = false,
  entityKybApproved = false,
  hasValutAccount = true,
}) => {
  if (!situation) return null;

  const tone = situation.tone || "pending";
  const styles = TONE_STYLES[tone] ?? TONE_STYLES.pending;
  const label = resolveSituationLabel(situation);
  if (!label) return null;

  const verificationKey = situation.verificationStatus
    ? `baas.proposalSituation.verification.${String(situation.verificationStatus).toLowerCase()}`
    : null;
  const verificationLabel =
    verificationKey
    && intl.get(verificationKey)
    && intl.get(verificationKey) !== verificationKey
      ? intl.get(verificationKey)
      : null;

  const isPendingAccount =
    tone === "pending" || situation.verificationStatus === "PENDING";
  const summaryKey = !hasValutAccount
    ? "baas.proposalSituation.proposalWithoutValutAccount"
    : isPendingAccount && entityKybApproved
      ? "baas.proposalSituation.entityApprovedAccountPending"
      : isPendingAccount
        ? "baas.proposalSituation.accountPendingHint"
        : null;
  const summary =
    summaryKey
    && intl.get(summaryKey)
    && intl.get(summaryKey) !== summaryKey
      ? intl.get(summaryKey)
      : null;

  const sectionGap = compact ? "space-y-2" : "space-y-2.5";

  return (
    <div
      className={`rounded-xl border p-4 ${styles.card} ${className}`}
      role="status"
    >
      <div className={sectionGap}>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {intl.get("baas.proposalSituation.label")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex rounded-md px-2.5 py-1 text-xs font-semibold ${styles.badge}`}
          >
            {verificationLabel || label}
          </span>
          {situation.statusId != null ? (
            <span className={`text-xs font-medium ${styles.label}`}>
              {intl.get("baas.proposalSituation.statusId", { id: situation.statusId })}
            </span>
          ) : null}
        </div>
        {summary ? (
          <p className="text-sm leading-relaxed text-brand-dark dark:text-gray-100">{summary}</p>
        ) : !compact && label && verificationLabel && label !== verificationLabel ? (
          <p className="text-sm leading-relaxed text-brand-dark dark:text-gray-100">{label}</p>
        ) : !compact && !verificationLabel ? (
          <p className="text-sm font-medium leading-relaxed text-brand-dark dark:text-gray-100">{label}</p>
        ) : null}
      </div>
    </div>
  );
};

export default BaasProposalSituationBadge;
